import React, { useState, useEffect } from 'react';
import AdminLayout from './AdminLayout';
import { aboutAPI } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { useToast } from '../../hooks/use-toast';
import { Edit, Save, Trash2, Plus } from 'lucide-react';

const AdminAbout = () => {
  const { toast } = useToast();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingSection, setEditingSection] = useState(null);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await aboutAPI.get();
      setContent(response.data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load content",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveMainContent = async () => {
    try {
      await aboutAPI.update(content);
      toast({
        title: "Content saved",
        description: "Main content has been updated"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save content",
        variant: "destructive"
      });
    }
  };

  const handleSaveSection = async (sectionId) => {
    try {
      const section = content.sections.find(s => s.id === sectionId);
      await aboutAPI.updateSection(sectionId, section);
      toast({
        title: "Section saved",
        description: "Changes have been saved"
      });
      setEditingSection(null);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save section",
        variant: "destructive"
      });
    }
  };

  const handleDeleteSection = async (sectionId) => {
    try {
      await aboutAPI.deleteSection(sectionId);
      setContent({
        ...content,
        sections: content.sections.filter(s => s.id !== sectionId)
      });
      toast({
        title: "Section deleted",
        description: "The section has been removed"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete section",
        variant: "destructive"
      });
    }
  };

  const handleAddSection = async () => {
    const newSection = {
      id: Date.now(),
      title: "New Section",
      content: "Section content goes here..."
    };
    
    try {
      await aboutAPI.addSection(newSection);
      setContent({
        ...content,
        sections: [...content.sections, newSection]
      });
      setEditingSection(newSection.id);
      toast({
        title: "Section added",
        description: "New section has been created"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add section",
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="text-white">Loading...</div>
      </AdminLayout>
    );
  }

  if (!content) {
    return (
      <AdminLayout>
        <div className="text-white">Failed to load content</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-4xl">
        <h1 className="text-4xl font-light text-white mb-8">About Page Management</h1>

        <div className="space-y-8">
          <div className="border border-white/10 p-6">
            <h2 className="text-2xl font-light text-white mb-4">Main Content</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2 uppercase tracking-wider">Title</label>
                <Input
                  value={content.title}
                  onChange={(e) => setContent({ ...content, title: e.target.value })}
                  className="w-full bg-transparent border-white/10 focus:border-white text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2 uppercase tracking-wider">Subtitle</label>
                <Input
                  value={content.subtitle}
                  onChange={(e) => setContent({ ...content, subtitle: e.target.value })}
                  className="w-full bg-transparent border-white/10 focus:border-white text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2 uppercase tracking-wider">Description</label>
                <Textarea
                  value={content.description}
                  onChange={(e) => setContent({ ...content, description: e.target.value })}
                  rows={4}
                  className="w-full bg-transparent border-white/10 focus:border-white text-white resize-none"
                />
              </div>
            </div>
          </div>

          <div className="border border-white/10 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-light text-white">Sections</h2>
              <Button
                onClick={handleAddSection}
                className="bg-white text-black hover:bg-gray-200 transition-all duration-300"
              >
                <Plus size={16} className="mr-2" />
                Add Section
              </Button>
            </div>

            <div className="space-y-4">
              {content.sections.map((section) => (
                <div key={section.id} className="border border-white/10 p-4">
                  {editingSection === section.id ? (
                    <div className="space-y-4">
                      <Input
                        value={section.title}
                        onChange={(e) => {
                          setContent({
                            ...content,
                            sections: content.sections.map(s =>
                              s.id === section.id ? { ...s, title: e.target.value } : s
                            )
                          });
                        }}
                        className="w-full bg-transparent border-white/10 focus:border-white text-white"
                      />
                      <Textarea
                        value={section.content}
                        onChange={(e) => {
                          setContent({
                            ...content,
                            sections: content.sections.map(s =>
                              s.id === section.id ? { ...s, content: e.target.value } : s
                            )
                          });
                        }}
                        rows={4}
                        className="w-full bg-transparent border-white/10 focus:border-white text-white resize-none"
                      />
                      <Button
                        onClick={() => handleSaveSection(section.id)}
                        className="bg-white text-black hover:bg-gray-200"
                      >
                        <Save size={16} className="mr-2" />
                        Save
                      </Button>
                    </div>
                  ) : (
                    <div>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="text-xl font-light text-white mb-2">{section.title}</h3>
                          <p className="text-gray-400 text-sm">{section.content}</p>
                        </div>
                        <div className="flex space-x-2 ml-4">
                          <button
                            onClick={() => setEditingSection(section.id)}
                            className="text-gray-400 hover:text-white transition-colors duration-300"
                          >
                            <Edit size={18} />
                          </button>
                          <button
                            onClick={() => handleDeleteSection(section.id)}
                            className="text-gray-400 hover:text-red-400 transition-colors duration-300"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminAbout;
